import React, { useState, useEffect, useContext } from "react";
import { css } from "@emotion/react";
import { navigate } from "gatsby";
import { useCookies } from "react-cookie";

import { UserContext } from "../context/user";
import { FirebaseContext } from "../context/firebase";

import Layout from "../templates/siteLayout";
import Overlay from "../components/overlay";
import Seo from "../components/seo";
import TextInput from "../components/textInput";
import Button from "../components/button";
import LoginButton from "../features/login/loginButton";
import { InputLabel } from "../styles/common";
import Popup from "../components/popup";

import { baseColor1 } from "../styles/color";
import { breakpointMd } from "../styles/layout";
import { AmplitudeContext } from "../context/amplitude";

const formStyles = css`
  display: grid;
  align-items: end;
  gap: 3rem 2rem;
  margin: 2rem auto 4rem auto;

  h1 {
    margin: 0;
    align-self: start;
  }

  @media only screen and (min-width: ${breakpointMd}) {
    grid-template-columns: auto 400px;
    gap: 2rem;

    form {
      border: 2px solid ${baseColor1};
      border-radius: 1rem;
      padding: 1.5rem;
    }
  }
`;

const formInputStyles = css`
  position: relative;
  display: grid;
  row-gap: 1.5rem;
`;

const Login = () => {
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [emailError, setEmailError] = useState(undefined);
  const user = useContext(UserContext);
  const fb = useContext(FirebaseContext);
  const monitor = useContext(AmplitudeContext);
  const [cookies, setCookie, removeCookie] = useCookies(["lref"]);
  const [resubmit, setResubmit] = useState(false);

  useEffect(() => {
    monitor.logEvent("LOGIN PAGE VIEWED");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate form inputs
  useEffect(() => {
    const emailIsValid = /\S+@\S+\.\S+/.test(email);
    if (emailIsValid) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [setIsValid, email]);

  useEffect(() => {
    if (user === null) {
      if (fb.auth.isSignInWithEmailLink(window.location.href)) {
        // Read cookie to get signup data
        if (cookies.lref === undefined || cookies.lref.email === undefined) {
          // Set a flag to change the login form
          setResubmit(true);
        } else {
          // Authenticate the user
          fb.auth
            .signInWithEmailLink(cookies.lref.email, window.location.href)
            .then(() => {
              // Delete cookie, now that it is no longer needed
              removeCookie("lref", { path: "/" });

              // Anticipate that we would want this to point to the redirect
              // instead if someone accesses a private route prior to
              // logging in.
              navigate("/app");
            })
            .catch((error) => {
              const errorCode = error.code;
              const errorMessage = error.message;

              // This should provide handling for the various auth errors
              // Need to provide guidance
              // Common errors could be invalid email and invalid or expired OTPs
              console.log(errorCode, errorMessage);
            });
        }
      }
    } else if (user) {
      // Redirect to dashboard if the user is logged in already
      navigate("/app");
    }
  }, [user, fb, cookies, removeCookie]);

  const handleEmail = (e) => {
    setEmail(e.target.value);

    // Reset any errors when the user updates email
    setIsSent(false);
  };

  const handleSubmit = async () => {
    try {
      const response = await fb.auth.fetchSignInMethodsForEmail(email);
      const authenticated = response.length > 0 ? true : false;

      let actionCodeSettings = {
        url: window.location.origin + "/login",
        handleCodeInApp: true,
      };

      // If there is a valid email, send a magic link
      if (authenticated) {
        // Update state to show we're sending the link
        setIsSending(true);

        await fb.auth.sendSignInLinkToEmail(email, actionCodeSettings);
        setCookie(
          "lref",
          {
            email,
          },
          { path: "/" }
        );

        // Update state to show we've sent the link
        setIsSent(true);
        setIsSending(false);
      } else {
        // Write their email address to localStorage so we can retrieve it
        // on the signup page
        window.localStorage.setItem("emailForSignIn", email);

        // Redirect to the signup page
        navigate("/join");
      }
    } catch (error) {
      // Error results if Firebase is unavailable
      setEmailError(
        "We weren't able to connect for some reason. Please refresh and try again."
      );
    }
  };

  const handleResubmit = () => {
    setIsSending(true);
    // Complete signup when the cookie can't be found
    // Setting the cookie triggers the useEffect hook above
    setCookie(
      "lref",
      {
        email,
      },
      { path: "/" }
    );
  };

  const getResubmitButton = () => {
    if (isSending) {
      return <Button text="Signing in..." disabled overrideDisabledStyles />;
    } else {
      return (
        <Button
          text="Complete login"
          onClick={handleResubmit}
          disabled={!isValid}
        />
      );
    }
  };

  return (
    <>
      <Overlay />
      <Seo
        title="Log in"
        description="Sign into your account to find a meaningful career or candidates to advance your organization's mission."
      />
      <Layout>
        <div className="material-section">
          <Popup eventTriggered={true} />
          <section className="grid-item-100">
            <div css={formStyles}>
              <h1>
                It's great to see you again.{" "}
                <span className="green">Let's</span>{" "}
                <span className="yellow">hop</span>{" "}
                <span className="pink">to</span>{" "}
                <span className="blue">it</span>!
              </h1>
              <form
                css={formInputStyles}
                // This handles clicking the button or pressing the enter key.
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                {resubmit && (
                  <p>
                    Please confirm your information below to complete login.
                  </p>
                )}
                <div>
                  <InputLabel>Email address</InputLabel>
                  <TextInput
                    type="email"
                    placeholder="lara@onework.co"
                    fullWidth
                    value={email}
                    errorMsg={emailError}
                    onChange={handleEmail}
                  />
                </div>
                {resubmit ? (
                  getResubmitButton()
                ) : (
                  <LoginButton
                    type="submit"
                    isSending={isSending}
                    isSent={isSent}
                    isValid={isValid}
                  />
                )}
              </form>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
};

export default Login;
